import { Injectable } from '@angular/core';
import { Utils } from './utils';

export interface MfaPromptContext {
  message?: string;
  authentication_type?: string;
  email?: string;
}

/**
 * Shares one OTP entry across concurrent sensitive-action failures so parallel requests retry together.
 */
@Injectable({ providedIn: 'root' })
export class MfaOtpPromptService {
  visible = false;
  message = '';
  authentication_type: string | null = null;
  emailMasked: string | null = null;
  otpInput = '';

  private sharedPromise: Promise<string> | null = null;

  /** Resolves the current OTP request (wired from template). */
  submitFromDialog(): void {
    const code = (this.otpInput || '').replace(/\D/g, '');
    if (code.length !== 6 || !this.activeResolve) {
      return;
    }
    const resolve = this.activeResolve;
    this.activeResolve = null;
    this.activeReject = null;
    this.teardownUi();
    resolve(code);
  }

  /** Aborts current OTP prompt (wired from template). */
  cancelFromDialog(): void {
    if (!this.activeReject) {
      this.teardownUi();
      return;
    }
    const reject = this.activeReject;
    this.activeReject = null;
    this.activeResolve = null;
    this.teardownUi();
    reject(new DOMException('MFA cancelled', 'AbortError'));
  }

  prompt(ctx: MfaPromptContext): Promise<string> {
    if (!this.sharedPromise) {
      this.sharedPromise = new Promise<string>((resolve, reject) => {
        this.message =
          typeof ctx.message === 'string' && ctx.message.trim() !== '' ? ctx.message : 'Multi-factor authentication is required for this action.';
        this.authentication_type = ctx.authentication_type ?? null;
        this.emailMasked = ctx.email ? Utils.maskEmail(ctx.email) : null;
        this.otpInput = '';
        this.visible = true;
        this.activeResolve = resolve;
        this.activeReject = reject;
      }).finally(() => {
        this.sharedPromise = null;
        this.activeResolve = null;
        this.activeReject = null;
      });
    } else if (ctx.email || ctx.authentication_type || ctx.message) {
      // Same dialog session: refresh hint text if backend sent more metadata.
      if (ctx.authentication_type !== undefined) {
        this.authentication_type = ctx.authentication_type ?? null;
      }
      if (ctx.email !== undefined) {
        this.emailMasked = ctx.email ? Utils.maskEmail(ctx.email) : null;
      }
      if (typeof ctx.message === 'string' && ctx.message.trim() !== '') {
        this.message = ctx.message;
      }
    }

    return this.sharedPromise;
  }

  private activeResolve: null | ((value: string) => void) = null;
  private activeReject: null | ((reason?: unknown) => void) = null;

  private teardownUi(): void {
    this.visible = false;
    this.otpInput = '';
    this.message = '';
    this.authentication_type = null;
    this.emailMasked = null;
  }
}
